import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { isToday, isYesterday } from "date-fns";
import {
  Activity,
  AlertTriangle,
  CalendarOff,
  CheckSquare,
  ClipboardList,
  DollarSign,
  FileText,
  Heart,
  MessageSquare,
  Phone,
  Pill,
  Shield,
  Stethoscope,
  User,
  UserPlus,
} from "@/lib/icons";
import { useMemo, type ComponentType, type ReactNode } from "react";
import { formatTimestampInTimezone } from "@alm/domain";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { orpcClient, orpc } from "@/lib/orpc";
import { optionalDay, optionalOneOf, optionalString, KEEP_PLACE } from "@/lib/search";
import { cn } from "@/lib/utils";
import { tabByValue } from "./-components/chart/chart-tab-list";
import { ProfileSection } from "./-components/chart/profile-section";
import { SectionEmpty } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/section-empty";
import {
  categoriesForGroups,
  GROUP_DOT,
  groupLabel,
  groupOf,
  TIMELINE_GROUPS,
  type TimelineGroup,
} from "./-components/chart/timeline-groups";

type TimelineSearch = { range?: string; from?: string; to?: string; groups?: string };

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/timeline")({
  validateSearch: (search: Record<string, unknown>): TimelineSearch => {
    const groups = (optionalString(search.groups) ?? "")
      .split(",")
      .filter((id) => /^[a-z-]{1,30}$/.test(id));
    return {
      range: optionalOneOf(["7", "90", "all", "custom"], optionalString(search.range)),
      from: optionalDay(search.from),
      to: optionalDay(search.to),
      groups: groups.length ? groups.join(",") : undefined,
    };
  },
  component: TimelineTab,
});

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  profile: User,
  notes: MessageSquare,
  financial: DollarSign,
  medical: Stethoscope,
  medication: Pill,
  document: FileText,
  auth: Shield,
  attendance: CalendarOff,
  task: CheckSquare,
  admission: ClipboardList,
  incident: AlertTriangle,
  activity: Activity,
  care: Heart,
  lead: UserPlus,
  call: Phone,
};
const RANGES = [
  ["7", "7d"],
  ["30", "30d"],
  ["90", "90d"],
  ["all", "All"],
  ["custom", "Custom"],
] as const;

const dayKey = (iso: string, tz: string) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
const timeOf = (iso: string, tz: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(
    new Date(iso),
  );

/** Everything that has happened to this resident, newest first, by day. */
function TimelineTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const tz = resident.facilityTimezone ?? "UTC";
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<TimelineSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const range = params.range ?? "30";
  const setRange = (next: string) => patch({ range: next === "30" ? undefined : next });
  const customFrom = params.from ?? "";
  const customTo = params.to ?? "";
  const setCustomFrom = (next: string) => patch({ from: next || undefined });
  const setCustomTo = (next: string) => patch({ to: next || undefined });
  const groups = (params.groups?.split(",") ?? []) as TimelineGroup[];
  const setGroups = (next: TimelineGroup[]) => patch({ groups: next.join(",") || undefined });

  const { from, to } = useMemo(() => {
    if (range === "all") return { from: null, to: null };
    if (range === "custom")
      return {
        from: customFrom ? new Date(`${customFrom}T00:00:00`) : null,
        to: customTo ? new Date(`${customTo}T23:59:59.999`) : null,
      };
    const start = new Date();
    start.setDate(start.getDate() - Number(range));
    return { from: start, to: null };
  }, [range, customFrom, customTo]);
  const categories = categoriesForGroups(groups);

  const timeline = useInfiniteQuery({
    queryKey: [
      "resident-timeline",
      residentId,
      from?.toISOString(),
      to?.toISOString(),
      categories.join(","),
    ],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      orpcClient.care.residents.timeline(
        {
          residentId,
          limit: 50,
          cursor: pageParam,
          from,
          to,
          categories: categories.length ? categories : undefined,
        },
        { signal },
      ),
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor : undefined),
  });
  const events = timeline.data?.pages.flatMap((page) => page.events) ?? [];
  const counts = new Map<TimelineGroup, number>();
  for (const event of events)
    counts.set(groupOf(event.category), (counts.get(groupOf(event.category)) ?? 0) + 1);
  const days: { label: string; items: typeof events }[] = [];
  for (const event of events) {
    const date = new Date(event.occurredAt);
    const label = isToday(date)
      ? "Today"
      : isYesterday(date)
        ? "Yesterday"
        : dayKey(event.occurredAt, tz);
    const last = days.at(-1);
    if (last?.label === label) last.items.push(event);
    else days.push({ label, items: [event] });
  }
  const toggle = (group: TimelineGroup) =>
    setGroups(groups.includes(group) ? groups.filter((g) => g !== group) : [...groups, group]);

  return (
    <ProfileSection
      title="Timeline"
      description={
        events.length === 0
          ? "No events loaded"
          : `${events.length} ${events.length === 1 ? "event" : "events"} loaded, newest first`
      }
      action={
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            aria-label="Date range"
            value={range}
            onValueChange={(value) => (value ? setRange(value) : null)}
          >
            {RANGES.map(([value, label]) => (
              <ToggleGroupItem key={value} value={value}>
                {label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {range === "custom" ? (
            <span className="flex items-center gap-1">
              <Input
                type="date"
                className="h-8 w-36"
                aria-label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span className="text-muted-foreground text-xs">to</span>
              <Input
                type="date"
                className="h-8 w-36"
                aria-label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </span>
          ) : null}
        </div>
      }
    >
      <div className="border-border-soft mb-4 flex flex-wrap items-center gap-1.5 border-b pb-3">
        <FilterChip
          selected={groups.length === 0}
          count={events.length || undefined}
          onClick={() => setGroups([])}
        >
          All
        </FilterChip>
        {TIMELINE_GROUPS.map((group) => (
          <FilterChip
            key={group.id}
            selected={groups.includes(group.id)}
            count={counts.get(group.id)}
            onClick={() => toggle(group.id)}
          >
            {group.label}
          </FilterChip>
        ))}
      </div>

      {timeline.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : timeline.isError ? (
        <ReadFailed
          message="Could not load the timeline."
          error={timeline.error}
          onRetry={() => void timeline.refetch()}
          retrying={timeline.isFetching}
        />
      ) : events.length === 0 ? (
        <SectionEmpty
          title="No events in this range"
          detail="Widen the date range or clear a filter to see more of this resident's history."
        />
      ) : (
        <div className="flex flex-col gap-5">
          {days.map((day) => (
            <div key={day.label}>
              <p className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
                {day.label}
                <span className="bg-muted rounded-xs px-1.5 tabular-nums">{day.items.length}</span>
              </p>
              <ol className="mt-3">
                {day.items.map((event) => {
                  const group = groupOf(event.category);
                  const Icon = ICONS[event.category] ?? Activity;
                  const tab = event.link?.tab ? tabByValue(event.link.tab) : undefined;
                  const linked = Boolean(event.link?.href || tab);
                  const card = (
                    <div
                      className={cn(
                        // No frame per event: the rail and its dots already
                        // separate them, and forty boxes in a card was noise.
                        "rounded-md px-3 py-1",
                        linked && "hover:bg-accent transition-colors",
                      )}
                    >
                      <p className="text-sm font-medium">{event.title}</p>
                      {event.description ? (
                        <p className="text-muted-foreground mt-1 text-sm whitespace-pre-wrap">
                          {event.description}
                        </p>
                      ) : null}
                      <p className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="tabular-nums">{timeOf(event.occurredAt, tz)}</span>
                        {event.actorName ?? "System"}
                        {groupLabel(group)}
                      </p>
                    </div>
                  );
                  let body: ReactNode = card;
                  if (event.link?.href)
                    body = (
                      <a href={event.link.href} className="focus-ring block rounded-md">
                        {card}
                      </a>
                    );
                  else if (tab)
                    body = (
                      <Link
                        to={tab.to}
                        params={{ residentId }}
                        className="focus-ring block rounded-md"
                      >
                        {card}
                      </Link>
                    );
                  return (
                    <li key={event.id} className="flex gap-3 pb-3">
                      <span
                        className={cn(
                          "inline-flex size-7 shrink-0 items-center justify-center rounded-md",
                          GROUP_DOT[group],
                        )}
                      >
                        <Icon className="size-3.5" />
                      </span>
                      <div
                        className="min-w-0 flex-1"
                        title={formatTimestampInTimezone(event.occurredAt, tz)}
                      >
                        {body}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
          <div className="flex justify-center pt-1">
            {timeline.hasNextPage ? (
              <Button
                variant="outline"
                disabled={timeline.isFetchingNextPage}
                onClick={() => void timeline.fetchNextPage()}
              >
                {timeline.isFetchingNextPage ? "Loading…" : "Load older events"}
              </Button>
            ) : (
              <span className="text-muted-foreground text-xs">End of timeline</span>
            )}
          </div>
        </div>
      )}
    </ProfileSection>
  );
}
